import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Checkbox,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Fade,
  Stack,
  Tooltip,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Grow,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Radio as RadioIcon,
  Tv as TvIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import EmissorasService, {
  Emissora,
  EmissoraCreate,
  EmissoraUpdate,
  EmissoraFilter,
} from '../../services/emissorasService';

const EmissorasPage: React.FC = () => {
  const theme = useTheme();
  // Estado de listagem
  const [emissoras, setEmissoras] = useState<Emissora[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de filtros
  const [filter, setFilter] = useState<EmissoraFilter>({});
  
  // Lista fixa de meios e estados
  const meios = ["Televisão", "Rádio"];
  const estados = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO",
    "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI",
    "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
  ];

  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentEmissora, setCurrentEmissora] = useState<Emissora | null>(null);
  const [newEmissora, setNewEmissora] = useState<EmissoraCreate>({ nome: '' });
  const [editEmissora, setEditEmissora] = useState<EmissoraUpdate>({});

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Estado de visualização
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Estado para stepper/tabs do formulário
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [editActiveTab, setEditActiveTab] = useState(0);

  const fetchEmissoras = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      const data = await EmissorasService.getEmissoras(filter, skip, limit);
      setEmissoras(data);
      // Obs: Em uma API real, geralmente teríamos um count total para paginação
      // Mas vamos assumir que temos 100 registros para simplificar este exemplo
      setTotalCount(100);
    } catch (error) {
      console.error('Erro ao buscar emissoras:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar emissoras',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar emissoras ao iniciar a página ou mudar filtros/paginação
    fetchEmissoras();
  }, [fetchEmissoras]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof EmissoraFilter, value: string) => {
    setFilter((prev: EmissoraFilter) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));
    setPage(0); // Resetar paginação ao mudar filtro
  };

  const handleCreateEmissora = async () => {
    try {
      await EmissorasService.createEmissora(newEmissora);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Emissora criada com sucesso',
        severity: 'success',
      });
      // Limpar formulário
      setNewEmissora({ nome: '' });
      // Recarregar dados
      fetchEmissoras();
    } catch (error) {
      console.error('Erro ao criar emissora:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar emissora',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (emissora: Emissora) => {
    setCurrentEmissora(emissora);
    // Inicializar dados para edição
    setEditEmissora({
      nome: emissora.nome,
      rede: emissora.rede,
      meio: emissora.meio,
      cidade: emissora.cidade,
      estado: emissora.estado,
      prefixo: emissora.prefixo,
      codigo: emissora.codigo,
      valor: emissora.valor,
      grupos: emissora.grupos,
      streamurl: emissora.streamurl,
      gravar: emissora.gravar,
      diassemana: emissora.diassemana,
      horainicio: emissora.horainicio,
      horafim: emissora.horafim,
    });
    setEditActiveTab(0); // Reset para a primeira tab
    setOpenEditDialog(true);
  };

  const handleEditEmissora = async () => {
    if (!currentEmissora) return;

    try {
      await EmissorasService.updateEmissora(currentEmissora.id, editEmissora);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Emissora atualizada com sucesso',
        severity: 'success',
      });
      // Limpar estados
      setCurrentEmissora(null);
      setEditEmissora({});
      // Recarregar dados
      fetchEmissoras();
    } catch (error) {
      console.error('Erro ao atualizar emissora:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar emissora',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (emissora: Emissora) => {
    setCurrentEmissora(emissora);
    setOpenDeleteDialog(true);
  };

  const handleDeleteEmissora = async () => {
    if (!currentEmissora) return;

    try {
      await EmissorasService.deleteEmissora(currentEmissora.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Emissora removida com sucesso',
        severity: 'success',
      });
      // Limpar estado
      setCurrentEmissora(null);
      // Recarregar dados
      fetchEmissoras();
    } catch (error) {
      console.error('Erro ao remover emissora:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao remover emissora',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChangeNewEmissora = (field: string, value: any) => {
    setNewEmissora((prev: EmissoraCreate) => ({ ...prev, [field]: value }));
  };

  const handleChangeEditEmissora = (field: string, value: any) => {
    setEditEmissora((prev: EmissoraUpdate) => ({ ...prev, [field]: value }));
  };

  return (
    <SidebarLayout>
      <Box sx={{ 
        width: '100%', 
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Emissoras
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Gerencie as emissoras monitoradas.
          </Typography>
        </Box>

        {/* Filtros */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            width: '100%'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  sx: { borderRadius: 2 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Meio</InputLabel>
                <Select
                  value={filter.meio || ''}
                  onChange={(e: SelectChangeEvent) => handleFilterChange('meio', e.target.value)}
                  label="Meio"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {meios.map((meio: string) => (
                    <MenuItem key={meio} value={meio}>
                      {meio}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filter.estado || ''}
                  onChange={(e: SelectChangeEvent) => handleFilterChange('estado', e.target.value)}
                  label="Estado"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {estados.map((estado: string) => (
                    <MenuItem key={estado} value={estado}>
                      {estado}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={3} sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                  px: 3,
                  py: 1,
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                  }
                }}
              >
                Nova Emissora
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Lista de Emissoras */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            width: '100%'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={40} />
            </Box>
          ) : emissoras.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="textSecondary">
                Nenhuma emissora encontrada
              </Typography>
            </Box>
          ) : (
            <Fade in={!loading}>
              <TableContainer sx={{ width: '100%', overflow: 'auto' }}>
                <Table sx={{ 
                  width: '100%',
                  tableLayout: 'fixed',
                  [`& .${tableCellClasses.root}`]: {
                    borderBottom: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.06)',
                  },
                  [`& .${tableCellClasses.head}`]: {
                    fontWeight: 'bold',
                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width="25%" sx={{ 
                        py: 2, 
                        borderTopLeftRadius: 2, 
                        borderBottomLeftRadius: 0,
                      }}>Nome</TableCell>
                      <TableCell width="15%">Rede</TableCell>
                      <TableCell width="12%">Meio</TableCell>
                      <TableCell width="15%">Cidade</TableCell>
                      <TableCell width="8%">Estado</TableCell>
                      <TableCell width="12%">Prefixo</TableCell>
                      <TableCell width="13%" sx={{ 
                        borderTopRightRadius: 2, 
                        borderBottomRightRadius: 0, 
                        textAlign: 'center'
                      }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emissoras.map((emissora: Emissora) => (
                      <TableRow 
                        key={emissora.id}
                        hover
                        sx={{ 
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.03),
                          }
                        }}
                      >
                        <TableCell 
                          sx={{ 
                            fontWeight: 'medium',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {emissora.nome}
                        </TableCell>
                        <TableCell>{emissora.rede}</TableCell>
                        <TableCell>
                          <Chip 
                            icon={emissora.meio === 'Televisão' ? <TvIcon /> : <RadioIcon />}
                            label={emissora.meio} 
                            size="small"
                            sx={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 'medium',
                              '& .MuiChip-icon': { color: theme.palette.primary.main }
                            }}
                          />
                        </TableCell>
                        <TableCell>{emissora.cidade}</TableCell>
                        <TableCell>{emissora.estado}</TableCell>
                        <TableCell>{emissora.prefixo}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditDialog(emissora)}
                              sx={{ 
                                mr: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remover">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(emissora)}
                              sx={{ 
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Fade>
          )}
          <Box sx={{ mt: 3 }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
            />
          </Box>
        </Paper>

        {/* Dialog para Criar Nova Emissora */}
        <Dialog
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { 
              borderRadius: 2,
              overflow: 'hidden',
            }
          }}
        >
          <Box sx={{ 
            bgcolor: theme.palette.primary.main, 
            color: 'white',
            p: 2,
            display: 'flex',
            alignItems: 'center',
          }}>
            <Avatar sx={{ bgcolor: 'white', color: theme.palette.primary.main, mr: 2 }}>
              <AddIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              Nova Emissora
            </Typography>
          </Box>
          
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ maxHeight: '70vh', overflow: 'auto', pr: 1 }}>
              {/* Seção 1: Informações Básicas */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 'bold', 
                    display: 'flex', 
                    alignItems: 'center',
                    color: theme.palette.primary.main,
                    '&:after': {
                      content: '""',
                      height: '1px',
                      bgcolor: 'rgba(0,0,0,0.1)',
                      flex: 1,
                      ml: 2
                    }
                  }}
                >
                  Informações Básicas
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Nome da Emissora"
                      value={newEmissora.nome || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        handleChangeNewEmissora('nome', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Rede"
                      value={newEmissora.rede || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        handleChangeNewEmissora('rede', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Meio</InputLabel>
                      <Select
                        value={newEmissora.meio || ''}
                        onChange={(e: SelectChangeEvent) => 
                          handleChangeNewEmissora('meio', e.target.value)
                        }
                        label="Meio"
                        sx={{ borderRadius: 2 }}
                      >
                        {meios.map((meio: string) => (
                          <MenuItem key={meio} value={meio}>
                            {meio === 'Televisão' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TvIcon fontSize="small" sx={{ mr: 1 }} />
                                {meio}
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <RadioIcon fontSize="small" sx={{ mr: 1 }} />
                                {meio}
                              </Box>
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Cidade"
                      value={newEmissora.cidade || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        handleChangeNewEmissora('cidade', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Estado</InputLabel>
                      <Select
                        value={newEmissora.estado || ''}
                        onChange={(e: SelectChangeEvent) =>
                          handleChangeNewEmissora('estado', e.target.value)
                        }
                        label="Estado"
                        sx={{ borderRadius: 2 }}
                      >
                        {estados.map((estado: string) => (
                          <MenuItem key={estado} value={estado}>
                            {estado}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Prefixo"
                      value={newEmissora.prefixo || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeNewEmissora('prefixo', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Seção 2: Detalhes */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 'bold', 
                    display: 'flex', 
                    alignItems: 'center',
                    color: theme.palette.primary.main,
                    '&:after': {
                      content: '""',
                      height: '1px',
                      bgcolor: 'rgba(0,0,0,0.1)',
                      flex: 1,
                      ml: 2
                    }
                  }}
                >
                  Detalhes
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Código"
                      value={newEmissora.codigo || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeNewEmissora('codigo', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Grupos"
                      value={newEmissora.grupos || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeNewEmissora('grupos', e.target.value)
                      }
                      variant="outlined"
                      helperText="Separados por vírgula"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Valor"
                      type="number"
                      value={newEmissora.valor || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeNewEmissora('valor', e.target.value === '' ? '' : Number(e.target.value))
                      }
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="URL do Stream"
                      value={newEmissora.streamurl || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeNewEmissora('streamurl', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Seção 3: Configurações de Gravação */}
              <Box>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 'bold', 
                    display: 'flex', 
                    alignItems: 'center',
                    color: theme.palette.primary.main,
                    '&:after': {
                      content: '""',
                      height: '1px',
                      bgcolor: 'rgba(0,0,0,0.1)',
                      flex: 1,
                      ml: 2
                    }
                  }}
                >
                  Configurações de Gravação
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 3, 
                      p: 2, 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: 2 
                    }}>
                      <Typography variant="subtitle1" sx={{ mr: 2 }}>
                        Habilitar Gravação
                      </Typography>
                      <Switch
                        checked={newEmissora.gravar === true}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleChangeNewEmissora('gravar', e.target.checked)
                        }
                        color="primary"
                      />
                    </Box>
                  </Grid>
                  
                  {newEmissora.gravar && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Dias de Gravação
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          mb: 2 
                        }}>
                          {['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'].map((dia, index) => {
                            const isSelected = newEmissora.diassemana?.includes(index.toString()) || false;
                            return (
                              <Chip
                                key={index}
                                label={dia}
                                onClick={() => {
                                  const currentDays = newEmissora.diassemana?.split(',') || [];
                                  const dayIndex = index.toString();
                                  const newDays = currentDays.includes(dayIndex)
                                    ? currentDays.filter((d: string) => d !== dayIndex)
                                    : [...currentDays, dayIndex];
                                  handleChangeNewEmissora('diassemana', newDays.join(','));
                                }}
                                color={isSelected ? "primary" : "default"}
                                variant={isSelected ? "filled" : "outlined"}
                                sx={{ 
                                  borderRadius: '16px',
                                  transition: 'all 0.2s',
                                  cursor: 'pointer'
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Hora Início"
                          type="time"
                          value={newEmissora.horainicio || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChangeNewEmissora('horainicio', e.target.value)
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            step: 300, // 5 min
                          }}
                          sx={{ borderRadius: 2 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Hora Fim"
                          type="time"
                          value={newEmissora.horafim || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChangeNewEmissora('horafim', e.target.value)
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            step: 300, // 5 min
                          }}
                          sx={{ borderRadius: 2 }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
            <Button 
              onClick={() => setOpenCreateDialog(false)}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                px: 3
              }}
            >
              Cancelar
            </Button>
            
            <Button
              variant="contained"
              onClick={handleCreateEmissora}
              disabled={!newEmissora.nome || !newEmissora.rede || !newEmissora.meio || !newEmissora.cidade || !newEmissora.estado}
              sx={{ 
                borderRadius: 2,
                px: 4,
                bgcolor: theme.palette.success.main,
                '&:hover': {
                  bgcolor: theme.palette.success.dark
                }
              }}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog para Editar Emissora */}
        <Dialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { 
              borderRadius: 2,
              overflow: 'hidden',
            }
          }}
        >
          <Box sx={{ 
            bgcolor: theme.palette.primary.main, 
            color: 'white',
            p: 2,
            display: 'flex',
            alignItems: 'center',
          }}>
            <Avatar sx={{ bgcolor: 'white', color: theme.palette.primary.main, mr: 2 }}>
              <EditIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              Editar Emissora
            </Typography>
          </Box>
          
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ maxHeight: '70vh', overflow: 'auto', pr: 1 }}>
              {/* Seção 1: Informações Básicas */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 'bold', 
                    display: 'flex', 
                    alignItems: 'center',
                    color: theme.palette.primary.main,
                    '&:after': {
                      content: '""',
                      height: '1px',
                      bgcolor: 'rgba(0,0,0,0.1)',
                      flex: 1,
                      ml: 2
                    }
                  }}
                >
                  Informações Básicas
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Nome da Emissora"
                      value={editEmissora.nome || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        handleChangeEditEmissora('nome', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Rede"
                      value={editEmissora.rede || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        handleChangeEditEmissora('rede', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Meio</InputLabel>
                      <Select
                        value={editEmissora.meio || ''}
                        onChange={(e: SelectChangeEvent) => 
                          handleChangeEditEmissora('meio', e.target.value)
                        }
                        label="Meio"
                        sx={{ borderRadius: 2 }}
                      >
                        {meios.map((meio: string) => (
                          <MenuItem key={meio} value={meio}>
                            {meio === 'Televisão' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TvIcon fontSize="small" sx={{ mr: 1 }} />
                                {meio}
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <RadioIcon fontSize="small" sx={{ mr: 1 }} />
                                {meio}
                              </Box>
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Cidade"
                      value={editEmissora.cidade || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        handleChangeEditEmissora('cidade', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Estado</InputLabel>
                      <Select
                        value={editEmissora.estado || ''}
                        onChange={(e: SelectChangeEvent) =>
                          handleChangeEditEmissora('estado', e.target.value)
                        }
                        label="Estado"
                        sx={{ borderRadius: 2 }}
                      >
                        {estados.map((estado: string) => (
                          <MenuItem key={estado} value={estado}>
                            {estado}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Prefixo"
                      value={editEmissora.prefixo || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeEditEmissora('prefixo', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Seção 2: Detalhes */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 'bold', 
                    display: 'flex', 
                    alignItems: 'center',
                    color: theme.palette.primary.main,
                    '&:after': {
                      content: '""',
                      height: '1px',
                      bgcolor: 'rgba(0,0,0,0.1)',
                      flex: 1,
                      ml: 2
                    }
                  }}
                >
                  Detalhes
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Código"
                      value={editEmissora.codigo || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeEditEmissora('codigo', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Grupos"
                      value={editEmissora.grupos || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeEditEmissora('grupos', e.target.value)
                      }
                      variant="outlined"
                      helperText="Separados por vírgula"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Valor"
                      type="number"
                      value={editEmissora.valor || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeEditEmissora('valor', e.target.value === '' ? '' : Number(e.target.value))
                      }
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="URL do Stream"
                      value={editEmissora.streamurl || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeEditEmissora('streamurl', e.target.value)
                      }
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Seção 3: Configurações de Gravação */}
              <Box>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 'bold', 
                    display: 'flex', 
                    alignItems: 'center',
                    color: theme.palette.primary.main,
                    '&:after': {
                      content: '""',
                      height: '1px',
                      bgcolor: 'rgba(0,0,0,0.1)',
                      flex: 1,
                      ml: 2
                    }
                  }}
                >
                  Configurações de Gravação
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 3, 
                      p: 2, 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: 2 
                    }}>
                      <Typography variant="subtitle1" sx={{ mr: 2 }}>
                        Habilitar Gravação
                      </Typography>
                      <Switch
                        checked={editEmissora.gravar === true}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleChangeEditEmissora('gravar', e.target.checked)
                        }
                        color="primary"
                      />
                    </Box>
                  </Grid>
                  
                  {editEmissora.gravar && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Dias de Gravação
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          mb: 2 
                        }}>
                          {['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'].map((dia, index) => {
                            const isSelected = editEmissora.diassemana?.includes(index.toString()) || false;
                            return (
                              <Chip
                                key={index}
                                label={dia}
                                onClick={() => {
                                  const currentDays = editEmissora.diassemana?.split(',') || [];
                                  const dayIndex = index.toString();
                                  const newDays = currentDays.includes(dayIndex)
                                    ? currentDays.filter((d: string) => d !== dayIndex)
                                    : [...currentDays, dayIndex];
                                  handleChangeEditEmissora('diassemana', newDays.join(','));
                                }}
                                color={isSelected ? "primary" : "default"}
                                variant={isSelected ? "filled" : "outlined"}
                                sx={{ 
                                  borderRadius: '16px',
                                  transition: 'all 0.2s',
                                  cursor: 'pointer'
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Hora Início"
                          type="time"
                          value={editEmissora.horainicio || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChangeEditEmissora('horainicio', e.target.value)
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            step: 300, // 5 min
                          }}
                          sx={{ borderRadius: 2 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Hora Fim"
                          type="time"
                          value={editEmissora.horafim || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChangeEditEmissora('horafim', e.target.value)
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            step: 300, // 5 min
                          }}
                          sx={{ borderRadius: 2 }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
            <Button 
              onClick={() => setOpenEditDialog(false)}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                px: 3
              }}
            >
              Cancelar
            </Button>
            
            <Button
              variant="contained"
              onClick={handleEditEmissora}
              disabled={!editEmissora.nome || !editEmissora.rede || !editEmissora.meio || !editEmissora.cidade || !editEmissora.estado}
              sx={{ 
                borderRadius: 2,
                px: 4,
                bgcolor: theme.palette.success.main,
                '&:hover': {
                  bgcolor: theme.palette.success.dark
                }
              }}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog para Confirmar Exclusão */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja remover a emissora{' '}
              <strong>{currentEmissora?.nome}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenDeleteDialog(false)}
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>
            <Button 
              color="error" 
              onClick={handleDeleteEmissora}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              Remover
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para mensagens */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </SidebarLayout>
  );
};

export default EmissorasPage; 